import { Inject, Injectable } from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { Actions, Store, ofActionDispatched } from '@ngxs/store';
import { IManageAudienceUserActionPayload } from 'app/library/audience-users/interfaces/audience-user-action.action-payload.interface';
import { IManageAudienceUsersActionPayload } from 'app/library/audience-users/interfaces/audience-users-action.action-payload.interface';
import { AudienceUsersService } from 'app/library/audience-users/services/audience-users.service';
import { AudienceService } from 'app/library/audience/services/audience.service';
import { IManageExperienceUsersActionPayload } from 'app/library/experience-user/interfaces/experience-users-action.action-payload.interface';
import { ExperienceUsersService } from 'app/library/experience-users/services/experience-users.service';
import { ExperienceService } from 'app/library/experience/services/experience.service';
import { AdminPermissions } from 'app/library/feature/enums/admin-permissions.enum';
import { EngageMoveExperienceMembersDialogComponent } from 'app/library/grow/dialogs/engage-move-experience-members-dialog/engage-move-experience-members-dialog.component';
import { GrowMoveAudienceMembersDialogComponent } from 'app/library/grow/dialogs/grow-move-audience-members-dialog/grow-move-audience-members-dialog.component';
import {
    buildAudienceMembersDownloadRequest,
    buildAudienceMembersRemoveRequest,
} from 'app/library/grow/utils/build-audience-members-selection.util';
import {
    AddUsersToModulesAsManagers,
    AddUsersToModulesAsParticipantsAsync,
    AddUsersToModulesAsParticipantsAsyncSuccess,
    UpdateModuleUserResponsible,
} from 'app/library/module-user/actions';
import { TakeOverUsersToModulesDialogComponent } from 'app/library/module/components/dialogs/take-over-users-to-modules/take-over-users-to-modules.component';
import { Module } from 'app/library/module/models/module.model';
import { PasswordlessAccessDialogComponent } from 'app/library/passwordless-access/components/dialogs/passwordless-access-dialog/passwordless-access-dialog.component';
import { JobStatus } from 'app/library/shared/async-job-drawer/enums/job-status.enum';
import { IAddTeamLeadersActionPayload } from 'app/library/team/models/add-team-leaders-action.action.payload';
import { IAddTeamMembersActionPayload } from 'app/library/team/models/add-team-members-action.action.payload';
import { IAddTeamWatchersActionPayload } from 'app/library/team/models/add-team-watchers-action.action.payload';
import { Team } from 'app/library/team/models/team.model';
import { UserTeamRoleEnum } from 'app/library/team/models/user-team-role.enum';
import { TeamService } from 'app/library/team/services/team.service';
import { SelectTeamsDialogComponent } from 'app/main/pages/teams/dialogs/select-teams-dialog/select-teams-dialog.component';
import { SnackBarTime } from 'app/projects/core/src/lib/constants/snack-bar';
import { FUSE_ASYNC_DRAWER } from 'app/projects/fuse/src/lib/services/async-drawer.token';
import { MaxBrainConfirmDialogComponent } from 'app/projects/shared/src/lib/components/dialogs/confirm/confirm.component';
import { FeatureSwitchName } from 'app/projects/shared/src/lib/enums/feature-switch.enum';
import { FeatureSwitchPipe } from 'app/projects/shared/src/lib/pipes/feature-switch.pipe';
import { ResetPasswordsFailure } from 'app/projects/user/src/lib/actions/reset-passwords-failure.action';
import { ResetPasswordsSuccess } from 'app/projects/user/src/lib/actions/reset-passwords-success.action';
import { ResetPasswords } from 'app/projects/user/src/lib/actions/reset-passwords.action';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { UserApiService } from 'app/projects/user/src/lib/services/user.api-service';
import { AsyncJobApiService } from 'app/services/async-job.api-service';
import { firstValueFrom } from 'rxjs';
import { take } from 'rxjs/operators';

export interface IAudienceMembersActionsPermissions {
    canDownload: boolean;
    canCreatePasswordless: boolean;
    canAssignTeam: boolean;
    canAssignExperience: boolean;
    canAssignAudience: boolean;
    canAssignCourse: boolean;
    canResetPassword: boolean;
    canRemove: boolean;
    canShowActionsMenu: boolean;
    isTeamsFeatureEnabled: boolean;
    isEngageFeatureEnabled: boolean;
    isSSOFeatureEnabled: boolean;
    isPasswordlessLoginFeatureEnabled: boolean;
}

export interface IAudienceMembersActionsContext {
    audienceId: string;
    audienceName: string;
    searchQuery: string;
    selectedUserIds: string[];
    totalCount: number;
    onAfterAction?: () => void;
}

@Injectable()
export class AudienceMembersActionsService {
    constructor(
        private _audienceService: AudienceService,
        private _audienceUsersService: AudienceUsersService,
        private _experienceService: ExperienceService,
        private _experienceUsersService: ExperienceUsersService,
        private _teamService: TeamService,
        private _userApiService: UserApiService,
        private _store: Store,
        private _action$: Actions,
        private _featureSwitchPipe: FeatureSwitchPipe,
        private _matDialog: MatDialog,
        private _matSnackBar: MatSnackBar,
        private _translateService: TranslateService,
        @Inject(FUSE_ASYNC_DRAWER) private _asyncJobService: AsyncJobApiService
    ) {}

    getPermissions(): IAudienceMembersActionsPermissions {
        const permissions = this._store.selectSnapshot(UserState.getMyInfo)?.permissions || [];
        const isTenantAdmin = permissions.includes(AdminPermissions.TENANT_MANAGEMENT);
        const hasGrowWrite = isTenantAdmin || permissions.includes(AdminPermissions.GROW_WRITE);
        const isTeamsFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Teams);
        const isEngageFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Engage);
        const isSSOFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.Sso);
        const isPasswordlessLoginFeatureEnabled = this._featureSwitchPipe.transform(FeatureSwitchName.PasswordlessLogin);

        const canDownload = hasGrowWrite;
        const canCreatePasswordless = isTenantAdmin || (permissions.includes(AdminPermissions.PASSWORDLESS_ACCESS_WRITE) && hasGrowWrite);
        const canAssignTeam = hasGrowWrite && isTeamsFeatureEnabled;
        const canAssignExperience = hasGrowWrite && isEngageFeatureEnabled;
        const canAssignAudience = hasGrowWrite && isEngageFeatureEnabled;
        const canAssignCourse =
            isTenantAdmin ||
            (hasGrowWrite && (permissions.includes(AdminPermissions.COURSES_WRITE) || permissions.includes(AdminPermissions.MODULES_WRITE)));
        const canResetPassword = (isTenantAdmin || permissions.includes(AdminPermissions.USERS_WRITE)) && hasGrowWrite;
        const canRemove = hasGrowWrite;

        return {
            canDownload,
            canCreatePasswordless,
            canAssignTeam,
            canAssignExperience,
            canAssignAudience,
            canAssignCourse,
            canResetPassword,
            canRemove,
            canShowActionsMenu:
                canDownload ||
                canCreatePasswordless ||
                canAssignTeam ||
                canAssignExperience ||
                canAssignAudience ||
                canAssignCourse ||
                canResetPassword ||
                canRemove,
            isTeamsFeatureEnabled,
            isEngageFeatureEnabled,
            isSSOFeatureEnabled,
            isPasswordlessLoginFeatureEnabled,
        };
    }

    downloadUsers(context: IAudienceMembersActionsContext): void {
        if (!context.audienceId || !context.totalCount) {
            return;
        }

        const payload = buildAudienceMembersDownloadRequest(context.audienceId, context.selectedUserIds, context.searchQuery);
        this._userApiService.downloadUsers(payload, 'audience_members.xlsx').subscribe();
    }

    async openPasswordlessAccessDialog(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<void> {
        const ids = await this.resolveEffectiveUserIds(context, userIds);
        if (!ids.length) {
            return;
        }

        const dialogRef = this._matDialog.open(PasswordlessAccessDialogComponent, {
            disableClose: false,
            width: '770px',
        });
        dialogRef.componentInstance.selectedUserIds = ids;
    }

    async openAssignUserToTeamDialog(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<void> {
        const ids = await this.resolveEffectiveUserIds(context, userIds);
        if (!ids.length) {
            return;
        }

        await this._teamService.fetchRootTeams();

        const dialogRef = this._matDialog.open(SelectTeamsDialogComponent, {
            disableClose: false,
            width: '760px',
            autoFocus: false,
        });

        const rootTeams = this._teamService.getRootTeams();
        dialogRef.componentInstance.title = this._translateService.instant('USER.DIALOG.ASSIGN_TO_TEAM.TITLE', {
            userCount: ids.length,
        });
        dialogRef.componentInstance.listTree = rootTeams.map((team) => ({
            id: team.id,
            name: team.name,
            children: [],
            childrenCount: team.subteamCount || 0,
            parent: team.parent,
            isSelected: false,
            isParentSelected: false,
        }));
        dialogRef.componentInstance.showSelectRole = true;
        dialogRef.componentInstance.initDialog();

        dialogRef.afterClosed().subscribe(async (data: { selectedTeams: Team[]; selectedRole: number }) => {
            if (!data) {
                return;
            }

            const payload: IAddTeamLeadersActionPayload | IAddTeamMembersActionPayload | IAddTeamWatchersActionPayload = {
                userIds: ids,
                teamIds: data.selectedTeams.map((selectedTeam) => selectedTeam.id),
            };

            let result = null;
            switch (data.selectedRole) {
                case UserTeamRoleEnum.LEAD:
                    result = await this._teamService.addUsersToTeamAsLeaders(payload);
                    break;
                case UserTeamRoleEnum.MEMBER:
                    result = await this._teamService.addUsersToTeamAsMembers(payload);
                    break;
                case UserTeamRoleEnum.WATCHER:
                    result = await this._teamService.addUsersToTeamAsWatchers(payload);
                    break;
                default:
                    break;
            }

            if (
                result &&
                (data.selectedRole !== UserTeamRoleEnum.MEMBER ||
                    (result as { asyncJobId?: string; teamMembers?: unknown[] }).asyncJobId ||
                    ((result as { teamMembers?: unknown[] }).teamMembers?.length ?? 0) > 0)
            ) {
                this._matSnackBar.open(this._translateService.instant('USER.DIALOG.ASSIGN_TO_TEAM.MESSAGE.SUCCESS'), 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });
                context.onAfterAction?.();
            }
        });
    }

    async assignUsersToExperience(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<void> {
        const ids = await this.resolveEffectiveUserIds(context, userIds);
        if (!ids.length) {
            return;
        }

        let experiences = this._experienceService.getExperiences();
        if (!experiences) {
            await this._experienceService.fetchExperiences();
            experiences = this._experienceService.getExperiences();
        }

        const dialogRef = this._matDialog.open(EngageMoveExperienceMembersDialogComponent, {
            disableClose: false,
            width: '600px',
        });
        dialogRef.componentInstance.title = 'ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_EXPERIENCE.PLURAL';
        dialogRef.componentInstance.label = 'ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_EXPERIENCE.LABEL';
        dialogRef.componentInstance.experiences = experiences;

        dialogRef.afterClosed().subscribe(async (result: { target: string }) => {
            if (!result) {
                return;
            }

            const payload: IManageExperienceUsersActionPayload = {
                experience: { id: result.target },
                users: ids.map((id) => ({ id })),
            };

            const responsePayload = await this._experienceUsersService.addUsersToExperience(payload);
            if (responsePayload?.asyncJobId) {
                this._matSnackBar.open(this._translateService.instant('USER.MESSAGE.BULK_OPERATION_QUEUED'), 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });
            } else if (responsePayload) {
                this._matSnackBar.open(
                    this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_EXPERIENCE.MESSAGE.ASSIGN_SUCCESS', {
                        alreadyAssigned: responsePayload.already_assigned.length,
                        newlyAssigned: responsePayload.newly_assigned.length,
                    }),
                    'OK',
                    { verticalPosition: 'top', duration: SnackBarTime.medium }
                );
            }

            context.onAfterAction?.();
        });
    }

    async assignUsersToAudience(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<void> {
        const ids = await this.resolveEffectiveUserIds(context, userIds);
        if (!ids.length) {
            return;
        }

        let audience = this._audienceService.getAudiences();
        if (!audience) {
            await this._audienceService.fetchAudiences();
        }

        const dialogRef = this._matDialog.open(GrowMoveAudienceMembersDialogComponent, {
            disableClose: false,
            width: '680px',
            autoFocus: false,
        });

        const audiences = this._audienceService.getAudiences();
        const audienceListTree = this._audienceService.convertListToTree(audiences);
        dialogRef.componentInstance.title = 'ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.PLURAL';
        dialogRef.componentInstance.label = 'ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.LABEL';
        dialogRef.componentInstance.audienceListTree = audienceListTree[0];

        dialogRef.afterClosed().subscribe(async (result: IManageAudienceUsersActionPayload) => {
            if (!result) {
                return;
            }

            const payload: IManageAudienceUserActionPayload = {
                users: ids.map((id) => ({ id })) as [{ id: string }],
                audiences: [{ id: result.target }],
            };

            const responsePayload = await this._audienceUsersService.addUsersToAudience(payload);
            if (responsePayload.asyncJobId) {
                this._matSnackBar.open(this._translateService.instant('USER.MESSAGE.BULK_OPERATION_QUEUED'), 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.slow,
                });
            } else {
                this._matSnackBar.open(
                    this._translateService.instant('ENGAGE.ALL_USERS_TAB.DIALOGS.ASSIGN_AUDIENCE.MESSAGE.ASSIGN_USERS_SUCCESS', {
                        newlyAssignedUsersLength: responsePayload.newlyAssigned.length,
                        alreadyAssignedUsersLength: responsePayload.alreadyAssigned.length,
                        alreadyInChildrenUsersLength: responsePayload.alreadyInChildren.length,
                    }),
                    'OK',
                    { verticalPosition: 'top', duration: SnackBarTime.slow }
                );
            }

            context.onAfterAction?.();
        });
    }

    async assignUsersToCourse(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<void> {
        const ids = await this.resolveEffectiveUserIds(context, userIds);
        if (!ids.length) {
            return;
        }

        const dialogRef = this._matDialog.open(TakeOverUsersToModulesDialogComponent, {
            disableClose: false,
            width: '680px',
            autoFocus: false,
        });

        dialogRef.componentInstance.filteredUserIds = ids;
        dialogRef.componentInstance.title = this._translateService.instant('LIST.USER_MODULES.FIELD.CHOOSE_POSITION.TITLE', {
            usersCount: ids.length,
        });

        dialogRef.afterClosed().subscribe(
            (result: {
                data: {
                    moduleList: Module[];
                    userRole: 'participant' | 'manager' | 'responsible';
                    permissions: string[];
                    roles: { user_id: string; role: string }[];
                };
            }) => {
                if (!result?.data) {
                    return;
                }

                if (result.data.userRole === 'participant') {
                    this._store.dispatch(
                        new AddUsersToModulesAsParticipantsAsync({
                            moduleIds: result.data.moduleList.map((tempModule) => tempModule.id),
                            userIds: ids,
                        })
                    );
                    this._subscribeToParticipantAsyncJob();
                }

                if (result.data.userRole === 'manager') {
                    this._store.dispatch(
                        new AddUsersToModulesAsManagers({
                            moduleIds: result.data.moduleList.map((tempModule) => tempModule.id),
                            userIds: ids,
                            permissions: result.data.permissions,
                        })
                    );
                }

                if (result.data.userRole === 'responsible') {
                    this._store.dispatch(
                        new UpdateModuleUserResponsible({
                            moduleIds: result.data.moduleList.map((tempModule) => tempModule.id),
                            userIds: ids,
                            roles: result.data.roles,
                        })
                    );
                }

                context.onAfterAction?.();
            }
        );
    }

    async resetPasswords(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<void> {
        const ids = await this.resolveEffectiveUserIds(context, userIds);
        if (!ids.length) {
            return;
        }

        this._store.dispatch(new ResetPasswords(ids));
        this._action$.pipe(ofActionDispatched(ResetPasswordsSuccess, ResetPasswordsFailure), take(1)).subscribe((action) => {
            if (action instanceof ResetPasswordsSuccess) {
                this._matSnackBar.open(this._translateService.instant('USER.MESSAGE.RESET_PASSWORDS_SUCCESS', { total: ids.length }), 'OK', {
                    verticalPosition: 'top',
                    duration: SnackBarTime.medium,
                });
            }
            context.onAfterAction?.();
        });
    }

    removeUsers(context: IAudienceMembersActionsContext, userIds?: string[]): void {
        if (!context.audienceId || !context.totalCount) {
            return;
        }

        const selectedIds = userIds?.length ? userIds : context.selectedUserIds;
        const isSingular = selectedIds.length === 1;

        const confirmDialogRef: MatDialogRef<MaxBrainConfirmDialogComponent> = this._matDialog.open(MaxBrainConfirmDialogComponent, {
            disableClose: false,
            width: '600px',
        });
        confirmDialogRef.componentInstance.confirmTitle = isSingular
            ? 'ENGAGE.AUDIENCE.DIALOGS.REMOVE_USER.TITLE.SINGULAR'
            : 'ENGAGE.AUDIENCE.DIALOGS.REMOVE_USER.TITLE.PLURAL';
        confirmDialogRef.componentInstance.confirmButtonColor = 'warn';
        confirmDialogRef.componentInstance.confirmMessage = this._translateService.instant(
            isSingular ? 'ENGAGE.AUDIENCE.DIALOGS.REMOVE_USER.MESSAGE.SINGULAR' : 'ENGAGE.AUDIENCE.DIALOGS.REMOVE_USER.MESSAGE.PLURAL',
            { audienceName: context.audienceName }
        );
        confirmDialogRef.componentInstance.actionButtonText = 'GENERAL.BUTTON.REMOVE';

        confirmDialogRef.afterClosed().subscribe((result) => {
            if (!result) {
                return;
            }

            const payload = buildAudienceMembersRemoveRequest(selectedIds, context.searchQuery);
            this._audienceService.removeAudienceUsers(context.audienceId, payload).subscribe({
                next: (response) => {
                    if (response.asyncJobId) {
                        this._matSnackBar.open(this._translateService.instant('USER.MESSAGE.BULK_OPERATION_QUEUED'), 'OK', {
                            verticalPosition: 'top',
                            duration: SnackBarTime.medium,
                        });
                    }
                    context.onAfterAction?.();
                },
            });
        });
    }

    async resolveEffectiveUserIds(context: IAudienceMembersActionsContext, userIds?: string[]): Promise<string[]> {
        if (userIds?.length) {
            return userIds;
        }

        if (context.selectedUserIds.length) {
            return context.selectedUserIds;
        }

        if (!context.audienceId || !context.totalCount) {
            return [];
        }

        const response = await firstValueFrom(
            this._audienceService.fetchAudienceMembers(context.audienceId, {
                page: 1,
                limit: context.totalCount,
                query: context.searchQuery?.trim() || undefined,
            })
        );

        return response.data.map((query) => String(query.id));
    }

    private _subscribeToParticipantAsyncJob(): void {
        this._action$.pipe(ofActionDispatched(AddUsersToModulesAsParticipantsAsyncSuccess), take(1)).subscribe(async (action: AddUsersToModulesAsParticipantsAsyncSuccess) => {
            const jobId = action.payload.asyncJobId;
            if (!jobId) {
                return;
            }

            this._asyncJobService.getAllJobs(true);
            this._asyncJobService.jobAdded.next(true);
            this._asyncJobService.numberOfActiveAsyncJobs++;

            let jobStatus = await this._asyncJobService.getJobStatus(jobId);
            if (jobStatus.communicatedStatus === JobStatus.Finished || jobStatus.communicatedStatus === JobStatus.TimedOut) {
                this._asyncJobService.getAllJobs(Boolean(localStorage.getItem('isDrawerOpen')));
                this._asyncJobService.completedJobs++;
                return;
            }

            const intervalId = setInterval(async () => {
                jobStatus = await this._asyncJobService.getJobStatus(jobId);
                this._asyncJobService.jobStatus.next(jobStatus);

                if (
                    jobStatus.communicatedStatus === JobStatus.Finished ||
                    jobStatus.communicatedStatus === JobStatus.TimedOut ||
                    jobStatus.communicatedStatus === JobStatus.Canceled
                ) {
                    this._asyncJobService.getAllJobs(Boolean(localStorage.getItem('isDrawerOpen')));
                    this._asyncJobService.completedJobs++;
                    clearInterval(intervalId);
                }
            }, 3000);
        });
    }
}
